import { Inject, Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { ENVIRONMENT, RUNTIME_ENVIRONMENT } from 'environments-lib';
import { IEnvironment, IRuntimeEnvironment } from 'interfaces-lib';
import { filter } from 'rxjs/operators';
import { IAnalyticsService } from './ianalyticsservice';
import { NoOpAnalyticsService } from './noop-analytics.service';

/**
 * Do NOT use this outside the 'ga' wrapper.
 */
declare var gtag: any;

@Injectable({
  providedIn: 'root',
})
export class GoogleAnalyticsService {
  private readonly googleAnalytics: string | undefined;

  private ga: IAnalyticsService | undefined;

  constructor(
    @Inject(ENVIRONMENT) protected environment: Partial<IEnvironment>,
    @Inject(RUNTIME_ENVIRONMENT) protected runtimeEnvironment: IRuntimeEnvironment | undefined,
    private router: Router
  ) {
    if (!this.runtimeEnvironment) {
      this.googleAnalytics = environment.googleAnalytics;
    } else {
      this.googleAnalytics =
        this.runtimeEnvironment.renderMode === 'web-app' ? environment.googleAnalyticsWebApp : environment.googleAnalyticsChromeExt;
    }
    if (environment.enableAnalytics) {
      this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe((e: NavigationEnd) => {
        gtag('js', new Date());
        gtag('config', environment.googleAnalytics);
      });
    }
  }

  init() {
    if (this.environment.enableAnalytics && this.runtimeEnvironment?.renderMode !== 'chrome-extension') {
      this.initialiseGoogleAnalytics();
    } else {
      this.ga = NoOpAnalyticsService;
    }

    this.uiInfo();
  }

  customEvent(eventName: string, value?: any, data?: {}): void {
    let eventData: any = {};
    if (data) {
      eventData = data;
    }
    if (value) {
      eventData.value = value;
    }

    this.ga?.recordEvent(eventName, eventData);
  }

  private initialiseGoogleAnalytics(): void {
    const script = document.createElement('script');
    script.src = 'https://www.googletagmanager.com/gtag/js?id=' + this.googleAnalytics;
    script.async = true;
    document.getElementsByTagName('head')[0].appendChild(script);

    const gtagEl = document.createElement('script');
    const gtagBody = document.createTextNode(
      `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', '` +
        this.googleAnalytics +
        `');
    `
    );
    gtagEl.appendChild(gtagBody);
    document.body.appendChild(gtagEl);

    // these calls are ok outside the ga wrapper and inside the initialisation block
    gtag('js', new Date());
    gtag('config', this.googleAnalytics);

    // wrapper around the gtag function
    this.ga = {
      recordEvent(eventType: string, eventData: object) {
        gtag('event', eventType, eventData);
      },
      recordError(error: any) {
        gtag('event', 'ERROR', error);
      },
    };
  }

  private uiInfo(): void {
    this.reportViewportSize();
    this.darkMode();
    this.customEvent('kp_app_version', this.environment.version);
    this.customEvent('screen_scaling', window.devicePixelRatio * 100 + '%');
  }

  private reportViewportSize(): void {
    let width = window.innerWidth;
    let height = window.innerHeight;

    width = Math.round(width / 100) * 100;
    height = Math.round(height / 100) * 100;

    const size = width + 'x' + height;
    this.customEvent('viewport_size', size);
  }

  private darkMode(): void {
    const mode = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches ? 'darkMode' : 'lightMode';
    this.customEvent(mode);
  }
}
